/**
 * RFC-149: FeatureCard — icon, title, description. Replaces inline duplicates.
 */
import type { ComponentType, ReactNode } from 'react';
import { MotionReveal } from './MotionReveal';

interface FeatureCardProps {
  icon?: ComponentType<{ className?: string }>;
  emoji?: string;
  title: ReactNode;
  description: ReactNode;
  /** Optional larger card with custom inner padding. */
  size?: 'default' | 'large';
  /** Stagger reveal by index when grid mapping. */
  delayMs?: number;
  className?: string;
}

export function FeatureCard({
  icon: Icon,
  emoji,
  title,
  description,
  size = 'default',
  delayMs,
  className,
}: FeatureCardProps) {
  const padding = size === 'large' ? 'p-8' : 'p-6';
  return (
    <MotionReveal
      as="article"
      delayMs={delayMs}
      className={`bg-card rounded-2xl ${padding} shadow-card hover:shadow-card-hover transition-shadow border border-border ${className ?? ''}`}
    >
      {(Icon || emoji) && (
        <div className="w-12 h-12 rounded-xl gradient-primary flex items-center justify-center mb-4">
          {Icon ? (
            <Icon className="w-6 h-6 text-primary-foreground" />
          ) : (
            <span aria-hidden className="text-2xl">{emoji}</span>
          )}
        </div>
      )}
      <h3 className="font-display text-lg font-semibold text-foreground mb-2">{title}</h3>
      <p className="text-muted-foreground text-sm leading-relaxed">{description}</p>
    </MotionReveal>
  );
}
